import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocalizedPath, getRouteByKey, isRouteAvailable, type SiteLanguage } from "@/utils/routing";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { useRotatingCTA } from "@/hooks/useRotatingCTA";
import { QuizHeaderMenu } from "@/components/quizMenu/QuizHeaderMenu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBranchesOpen, setIsBranchesOpen] = useState(false);
  const [isMobileBranchesOpen, setIsMobileBranchesOpen] = useState(false);
  const branchesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { ctaIndex, rotatingCTAs, getAppUrl } = useRotatingCTA();

  // Map language to logo
  const logoMap: Record<string, string> = {
    sk: '/images/bidmio-logo-sk.png',
    cs: '/images/bidmio-logo-cs.png',
    da: '/images/bidmio-logo-da.png',
    en: '/images/bidmio-logo-en.png',
    de: '/images/bidmio-logo-de.png',
  };
  
  const currentLogo = logoMap[i18n.language] || '/images/bidmio-logo-sk.png';

  const lang = i18n.language as SiteLanguage;

  // Trade landing pages — filtered by per-locale route availability, never hardcoded.
  const branchLinks = [
    { routeKey: "tradeElectrician", name: t("layout.header.nav.trades.electrician") },
    { routeKey: "tradePlumber", name: t("layout.header.nav.trades.plumber") },
    { routeKey: "tradePainter", name: t("layout.header.nav.trades.painter") },
    { routeKey: "tradeCarpenter", name: t("layout.header.nav.trades.carpenter") },
    { routeKey: "tradeMason", name: t("layout.header.nav.trades.mason") },
    { routeKey: "tradeRoofer", name: t("layout.header.nav.trades.roofer") },
    { routeKey: "tradeSewer", name: t("layout.header.nav.trades.sewer") },
    { routeKey: "tradeGeneralContractor", name: t("layout.header.nav.trades.generalContractor") },
    { routeKey: "tradeService", name: t("layout.header.nav.trades.service") },
    { routeKey: "forAccountants", name: t("layout.header.nav.trades.accountants") },
  ]
    .filter(({ routeKey }) => isRouteAvailable(routeKey, lang))
    .map(({ routeKey, name }) => ({ name, href: getRouteByKey(routeKey, lang) }));

  const isBranchActive = branchLinks.some((link) => link.href === location.pathname);

  const navigationBefore = [
    { name: t("layout.header.nav.home"), href: getLocalizedPath("/", lang) },
    { name: t("layout.header.nav.modules"), href: getLocalizedPath("/moduly", lang) },
    { name: t("layout.header.nav.atlas"), href: getRouteByKey("productAtlas", lang) },
    { name: t("layout.header.nav.pricing"), href: getLocalizedPath("/cennik", lang) },
  ];
  const navigationAfter = [
    ...(isRouteAvailable("soloOffer", lang)
      ? [{
          name: t("layout.header.nav.solo"),
          href: getRouteByKey("soloOffer", lang),
          testId: "link-header-solo-offer",
        }]
      : []),
    { name: t("layout.header.nav.about"), href: getLocalizedPath("/o-nas", lang) },
    { name: t("layout.header.nav.contact"), href: getLocalizedPath("/kontakt", lang) },
  ];

  // Close the desktop branches dropdown on Escape or outside click.
  // The effect attaches listeners client-side only, so SSR is unaffected.
  useEffect(() => {
    if (!isBranchesOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsBranchesOpen(false);
    };
    const onMouseDown = (event: MouseEvent) => {
      if (branchesRef.current && !branchesRef.current.contains(event.target as Node)) {
        setIsBranchesOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [isBranchesOpen]);

  return (
    <header
      className="fixed left-0 right-0 z-50 glass-card border-b transition-[top] duration-200"
      style={{ top: 'var(--announcement-h, 36px)' }}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <picture>
              <source type="image/webp" srcSet={currentLogo.replace(/\.png$/, ".webp")} />
              <img
                src={currentLogo}
                alt={t("layout.header.logoAlt")}
                className="h-12 w-auto"
              />
            </picture>
          </Link>

          {/* Desktop Navigation — shown only from xl up; eight items plus logo
              and CTA do not fit between md (768px) and xl (1280px). */}
          <nav className="hidden xl:flex items-center space-x-8">
            {navigationBefore.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium ${
                  location.pathname === item.href ? 'text-primary' : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
            {branchLinks.length > 0 && (
              <div
                ref={branchesRef}
                className="relative"
                onMouseEnter={() => setIsBranchesOpen(true)}
                onMouseLeave={() => setIsBranchesOpen(false)}
              >
                <button
                  type="button"
                  aria-expanded={isBranchesOpen}
                  aria-haspopup="true"
                  onClick={() => setIsBranchesOpen(true)}
                  data-testid="button-header-branches"
                  className={`flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium ${
                    isBranchActive ? 'text-primary' : ''
                  }`}
                >
                  {t("layout.header.nav.branches")}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isBranchesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isBranchesOpen && (
                  <div className="absolute left-0 top-full pt-2 z-50">
                    <div className="min-w-[220px] rounded-lg border border-glass-border bg-background/95 backdrop-blur-md shadow-lg py-2">
                      {branchLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setIsBranchesOpen(false)}
                          className={`block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200 ${
                            location.pathname === link.href ? 'text-primary' : ''
                          }`}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {navigationAfter.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                data-testid={"testId" in item ? item.testId : undefined}
                className={`text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium ${
                  location.pathname === item.href ? 'text-primary' : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden xl:flex items-center gap-4">
            <QuizHeaderMenu />
            <a 
              href={getAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="button-app-access"
            >
              <Button 
                className="relative overflow-hidden bg-gradient-to-r from-primary via-primary-hover to-primary hover:from-primary-hover hover:via-primary hover:to-primary-hover text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg group"
              >
                <span className="relative">
                  {/* Invisible spacer to reserve width of longest text */}
                  <span className="invisible flex items-center gap-2">
                    {rotatingCTAs.reduce((a, b) => a.length > b.length ? a : b)}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                  {/* Actual visible rotating text */}
                  <span className="absolute inset-0 flex items-center justify-center gap-2">
                    <span 
                      key={ctaIndex}
                      className="animate-in fade-in slide-in-from-bottom-2 duration-500"
                    >
                      {rotatingCTAs[ctaIndex]}
                    </span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </span>
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="xl:hidden p-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={t("layout.header.mobileMenuAriaLabel")}
            data-testid="button-mobile-menu"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            className="xl:hidden py-6 border-t border-glass-border bg-background/95 backdrop-blur-md overflow-y-auto"
            style={{ maxHeight: 'calc(100dvh - 4rem - var(--announcement-h, 36px))' }}
          >
            <nav className="flex flex-col space-y-6 px-2">
              {navigationBefore.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium py-3 px-4 rounded-lg hover:bg-muted/50 touch-manipulation min-h-[44px] flex items-center ${
                    location.pathname === item.href ? 'text-primary bg-primary/10' : ''
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              {branchLinks.length > 0 && (
                <div>
                  <button
                    type="button"
                    aria-expanded={isMobileBranchesOpen}
                    onClick={() => setIsMobileBranchesOpen((open) => !open)}
                    data-testid="button-header-branches-mobile"
                    className={`w-full text-left text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium py-3 px-4 rounded-lg hover:bg-muted/50 touch-manipulation min-h-[44px] flex items-center justify-between ${
                      isBranchActive ? 'text-primary bg-primary/10' : ''
                    }`}
                  >
                    {t("layout.header.nav.branches")}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMobileBranchesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMobileBranchesOpen && (
                    <div className="flex flex-col space-y-2 mt-2">
                      {branchLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium py-3 pr-4 pl-8 rounded-lg hover:bg-muted/50 touch-manipulation min-h-[44px] flex items-center ${
                            location.pathname === link.href ? 'text-primary bg-primary/10' : ''
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {navigationAfter.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={"testId" in item ? item.testId : undefined}
                  className={`text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium py-3 px-4 rounded-lg hover:bg-muted/50 touch-manipulation min-h-[44px] flex items-center ${
                    location.pathname === item.href ? 'text-primary bg-primary/10' : ''
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <a 
                href={getAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4"
                onClick={() => setIsMenuOpen(false)}
                data-testid="button-app-access-mobile"
              >
                <Button 
                  className="relative w-full overflow-hidden bg-gradient-to-r from-primary via-primary-hover to-primary hover:from-primary-hover hover:via-primary hover:to-primary-hover text-white font-semibold h-12 text-base transition-all duration-300 group"
                >
                  <span className="relative">
                    {/* Invisible spacer to reserve width of longest text */}
                    <span className="invisible flex items-center justify-center gap-2 w-full">
                      {rotatingCTAs.reduce((a, b) => a.length > b.length ? a : b)}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                    {/* Actual visible rotating text */}
                    <span className="absolute inset-0 flex items-center justify-center gap-2">
                      <span 
                        key={ctaIndex}
                        className="animate-in fade-in slide-in-from-bottom-2 duration-500"
                      >
                        {rotatingCTAs[ctaIndex]}
                      </span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </span>
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;